/* Healing Herbals display-currency converter
 * Shows storefront prices in the shopper's currency. Checkout still settles in USD
 * (Authorize.net is USD-only), so every converted figure is a reference price and
 * the notice says so.
 * Rates: Frankfurter v1 (ECB reference rates) with open.er-api.com as fallback, cached 12h
 * in localStorage; one small JSON fetch per 12h, no images, no cart interception.
 * If every rate source fails the page simply stays in USD.
 * 11-3-26: api.frankfurter.app died (401/CORS) so the converter had been silently off; moved to
 * api.frankfurter.dev/v1 + fallback. Switcher now lives in the top announcement bar (small, text only)
 * plus the footer copy. No floating bubble.
 */
(function () {
  'use strict';
  var cfg = window.HHCurrency || {};
  var BASE = 'USD';
  var SUPPORTED = cfg.currencies || ['USD', 'AUD', 'CAD', 'GBP', 'EUR', 'NZD', 'JPY', 'CHF', 'SEK', 'MXN'];
  var COUNTRY_TO_CUR = {
    AU: 'AUD', CA: 'CAD', GB: 'GBP', NZ: 'NZD', JP: 'JPY', CH: 'CHF', SE: 'SEK', MX: 'MXN',
    AT: 'EUR', BE: 'EUR', DE: 'EUR', ES: 'EUR', FI: 'EUR', FR: 'EUR', IE: 'EUR', IT: 'EUR',
    NL: 'EUR', PT: 'EUR', GR: 'EUR', LU: 'EUR', SK: 'EUR', SI: 'EUR', EE: 'EUR', LV: 'EUR',
    LT: 'EUR', CY: 'EUR', MT: 'EUR', HR: 'EUR'
  };
  var LABELS = { USD: 'US$', AUD: 'A$', CAD: 'C$', NZD: 'NZ$', GBP: '£', EUR: '€', JPY: '¥', CHF: 'CHF', SEK: 'kr', MXN: 'MX$' };
  var SYMS = SUPPORTED.filter(function (c) { return c !== BASE; });
  var RATE_SOURCES = [
    { url: 'https://api.frankfurter.dev/v1/latest?base=USD&symbols=' + SYMS.join(','), pick: function (j) { return j && j.rates; } },
    { url: 'https://open.er-api.com/v6/latest/USD', pick: function (j) { if (!j || !j.rates) return null; var o = {}; SYMS.forEach(function (c) { if (j.rates[c]) o[c] = j.rates[c]; }); return o; } }
  ];
  var LS_RATES = 'hh_cur_rates_v2';
  var LS_CHOICE = 'hh_cur_choice_v1';
  var TTL = 12 * 60 * 60 * 1000;
  var USD_RE = /^\s*(From\s+)?\$\s?(\d{1,3}(?:,\d{3})*|\d+)(\.\d{2})?\s*(USD)?\s*$/i;
  var PRICE_SELECTOR = 'sale-price, compare-at-price, unit-price, [data-hh-money]';
  var rates = null;
  var current = BASE;

  function ls(k, v) {
    try {
      if (v === undefined) return localStorage.getItem(k);
      localStorage.setItem(k, v);
    } catch (e) { return null; }
  }

  function pickDefault() {
    var saved = ls(LS_CHOICE);
    if (saved && SUPPORTED.indexOf(saved) !== -1) return saved;
    var cc = (cfg.country || '').toUpperCase();
    var guess = COUNTRY_TO_CUR[cc];
    return guess && SUPPORTED.indexOf(guess) !== -1 ? guess : BASE;
  }

  function fetchFrom(i) {
    if (i >= RATE_SOURCES.length) return Promise.resolve(null);
    var src = RATE_SOURCES[i];
    return fetch(src.url, { cache: 'no-store' })
      .then(function (r) { if (!r.ok) throw new Error('rate fetch ' + r.status); return r.json(); })
      .then(function (j) { var got = src.pick(j); if (!got || !Object.keys(got).length) throw new Error('empty'); return got; })
      .catch(function () { return fetchFrom(i + 1); });
  }

  function loadRates() {
    var cached = ls(LS_RATES);
    if (cached) {
      try {
        var obj = JSON.parse(cached);
        if (obj && obj.t && Date.now() - obj.t < TTL && obj.rates) { rates = obj.rates; return Promise.resolve(rates); }
      } catch (e) {}
    }
    // Only hit the network when the shopper is not on USD (saves a request for most US visitors)
    if (current === BASE) return Promise.resolve(null);
    return fetchFrom(0).then(function (got) {
      rates = got || null;
      if (rates) ls(LS_RATES, JSON.stringify({ t: Date.now(), rates: rates }));
      return rates;
    });
  }

  function fmt(amountUsd, cur) {
    if (cur === BASE) return null;
    if (!rates || !rates[cur]) return null;
    var v = amountUsd * rates[cur];
    var decimals = cur === 'JPY' ? 0 : 2;
    var num;
    try {
      num = new Intl.NumberFormat(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(v);
    } catch (e) { num = v.toFixed(decimals); }
    return (LABELS[cur] || cur + ' ') + num;
  }

  function usdFromText(t) {
    var m = USD_RE.exec(t);
    if (!m) return null;
    return parseFloat(m[2].replace(/,/g, '') + (m[3] || '.00'));
  }
  function prefixOf(t) {
    var m = USD_RE.exec(t);
    return m && m[1] ? m[1].trim() + ' ' : '';
  }

  function visibleText(el) {
    var out = '';
    el.childNodes.forEach(function (n) {
      if (n.nodeType === 3) out += n.nodeValue;
      else if (n.nodeType === 1 && !n.classList.contains('sr-only') && !n.classList.contains('hh-cur')) out += n.textContent;
      else if (n.nodeType === 1 && n.classList.contains('hh-cur')) out += n.getAttribute('data-usd-text') || '';
    });
    return out;
  }

  function convertEl(el) {
    var already = el.querySelector(':scope > .hh-cur');
    var usd, usdText;
    if (already) {
      usd = parseFloat(already.getAttribute('data-usd'));
      usdText = already.getAttribute('data-usd-text');
    } else {
      usdText = visibleText(el);
      usd = usdFromText(usdText);
      if (usd === null) return;
    }
    var converted = fmt(usd, current);
    if (converted) converted = prefixOf(usdText) + converted;
    if (!already) {
      if (!converted) return; // on USD: leave the theme's markup untouched
      var keep = [];
      Array.prototype.slice.call(el.childNodes).forEach(function (n) {
        if (n.nodeType === 1 && n.classList.contains('sr-only')) keep.push(n); else el.removeChild(n);
      });
      already = document.createElement('span');
      already.className = 'hh-cur';
      already.setAttribute('data-usd', String(usd));
      already.setAttribute('data-usd-text', usdText.trim());
      keep.forEach(function (n) { el.appendChild(n); });
      el.appendChild(already);
    }
    var want = converted || usdText.trim();
    if (already.textContent === want) return;
    if (converted) {
      already.textContent = converted;
      already.setAttribute('title', usdText.trim() + ' USD');
    } else {
      already.textContent = usdText.trim();
      already.removeAttribute('title');
    }
  }

  function convertAll(root) {
    (root || document).querySelectorAll(PRICE_SELECTOR).forEach(convertEl);
    updateNotices();
  }

  function updateNotices() {
    var show = current !== BASE && rates && rates[current];
    var existing = document.querySelectorAll('.hh-cur-notice');
    var hosts = document.querySelectorAll('price-list.price-list--product, .cart-drawer__footer, .cart__footer, .cart-summary, .order-summary');
    if (show && existing.length === hosts.length && existing.length && existing[0].getAttribute('data-cur') === current) return;
    if (!show && !existing.length) return;
    existing.forEach(function (n) { n.remove(); });
    if (!show) return;
    var text = cfg.noticeText || 'Prices shown in {cur} for reference. You will be charged in US dollars at checkout; your bank sets the final exchange rate.';
    text = text.replace('{cur}', current);
    hosts.forEach(function (host) {
      var n = document.createElement('div');
      n.className = 'hh-cur-notice';
      n.setAttribute('data-cur', current);
      n.textContent = text;
      host.insertAdjacentElement('afterend', n);
    });
  }

  var observing = false;
  function setCurrency(cur) {
    if (SUPPORTED.indexOf(cur) === -1) cur = BASE;
    current = cur;
    ls(LS_CHOICE, cur);
    document.querySelectorAll('.hh-cur-select').forEach(function (s) { s.value = cur; });
    if (cur !== BASE && !rates) {
      loadRates().then(function () {
        if (!rates) { current = BASE; document.querySelectorAll('.hh-cur-select').forEach(function (s) { s.value = BASE; }); return; }
        convertAll(); if (!observing) observe();
      });
      return;
    }
    convertAll();
    if (cur !== BASE && !observing) observe();
  }

  function makeSelect() {
    var sel = document.createElement('select');
    sel.className = 'hh-cur-select';
    sel.setAttribute('aria-label', 'Display currency');
    SUPPORTED.forEach(function (c) {
      var o = document.createElement('option');
      o.value = c; o.textContent = c;
      sel.appendChild(o);
    });
    sel.value = current;
    sel.addEventListener('change', function () { setCurrency(sel.value); });
    return sel;
  }

  function buildSwitcher() {
    if (document.querySelector('.hh-cur-switcher')) return;
    // Top: a small text-only select pinned to the right of the announcement bar
    var bar = document.querySelector('.announcement-bar');
    if (bar) {
      var top = document.createElement('div');
      top.className = 'hh-cur-switcher hh-cur-switcher--top';
      top.setAttribute('role', 'group');
      top.setAttribute('aria-label', 'Display currency');
      top.appendChild(makeSelect());
      bar.classList.add('hh-has-cur');
      bar.appendChild(top);
    }
    // Footer copy, same as before
    var footer = document.querySelector('footer .container, footer');
    if (footer && cfg.footerCopy !== false) {
      var w2 = document.createElement('div');
      w2.className = 'hh-cur-switcher hh-cur-switcher--footer';
      w2.setAttribute('role', 'group');
      w2.setAttribute('aria-label', 'Display currency');
      var label = document.createElement('span');
      label.className = 'hh-cur-switcher__label';
      label.textContent = 'Currency';
      w2.appendChild(label); w2.appendChild(makeSelect());
      footer.appendChild(w2);
    }
  }

  function observe() {
    observing = true;
    var pending = false;
    var opts = { childList: true, subtree: true, characterData: true };
    var mo = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        mo.disconnect();
        try { convertAll(); } finally { mo.takeRecords(); mo.observe(document.body, opts); }
      });
    });
    mo.observe(document.body, opts);
  }

  function init() {
    current = pickDefault();
    buildSwitcher();
    if (current === BASE) return; // US shopper: nothing else to do, zero network, zero DOM work
    loadRates().then(function () {
      if (!rates) {
        current = BASE;
        document.querySelectorAll('.hh-cur-select').forEach(function (s) { s.value = BASE; });
        return;
      }
      convertAll();
      observe();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
